/**
 * Configurazione centralizzata per tutti i tipi di item (Barche, Motori, Accessori)
 * 
 * Aggiungere un nuovo tipo richiede solo aggiungere una voce in questo file.
 * Tutti i componenti (ItemList, ItemCard, ItemDetail) useranno questa configurazione.
 */

export type ItemType = 'boats' | 'motors' | 'accessories';

export interface ItemConfig {
  // Endpoint API
  apiEndpoint: string;
  
  // Nomi singolare/plurale per UI
  singular: string;
  plural: string;
  singularCapitalized: string;
  pluralCapitalized: string;
  
  // Filtri specifici per tipo
  specificFilters: string[];
  
  // Campi specifici da mostrare nel dettaglio
  specificFields: string[];
  
  // Campi da mostrare nella card
  cardFields: CardField[];
  
  // Opzioni di ordinamento specifiche
  sortOptions: SortOption[];
  
  // Componente filtri da usare
  filterComponent: 'SidebarFilters' | 'MotorFilters' | 'AccessoryFilters';
  
  // Icona per il tipo
  icon: string;
  
  // Colore tema
  themeColor: string;
}

export interface CardField {
  key: string;
  label: string;
  format?: (value: any) => string;
  suffix?: string;
}

export interface SortOption {
  value: string;
  label: string;
}

export const itemConfigs: Record<ItemType, ItemConfig> = {
  boats: {
    apiEndpoint: '/items/boats',
    singular: 'barca',
    plural: 'barche',
    singularCapitalized: 'Barca',
    pluralCapitalized: 'Barche',
    
    specificFilters: ['min_length', 'max_length'],
    
    specificFields: ['length', 'beam', 'draft', 'cabins', 'berths', 'fuel_capacity'],
    
    cardFields: [
      // Priorità alta per cantiere nautico
      { key: 'length_m', label: 'Lunghezza', suffix: 'm' },
      { key: 'beam_m', label: 'Larghezza', suffix: 'm' },
      { key: 'cabins_count', label: 'Cabine' },
      { key: 'boat_type', label: 'Tipo' },
      { key: 'engines_brand', label: 'Motore' },
      { key: 'year', label: 'Anno' },
      { key: 'location_city', label: 'Località' },
      { key: 'dealer_price', label: 'Prezzo', format: (v) => `€${Number(v).toLocaleString('it-IT')}` },
    ],
    
    sortOptions: [
      { value: 'created_desc', label: 'Più recenti' },
      { value: 'created_asc', label: 'Meno recenti' },
      { value: 'price_asc', label: 'Prezzo crescente' },
      { value: 'price_desc', label: 'Prezzo decrescente' },
      { value: 'year_desc', label: 'Anno decrescente' },
      { value: 'length_desc', label: 'Lunghezza decrescente' },
      { value: 'distance_asc', label: 'Distanza crescente' },
    ],
    
    filterComponent: 'SidebarFilters',
    icon: 'anchor',
    themeColor: 'blue',
  },
  
  motors: {
    apiEndpoint: '/items/motors',
    singular: 'motore',
    plural: 'motori',
    singularCapitalized: 'Motore',
    pluralCapitalized: 'Motori',
    
    specificFilters: ['min_hours', 'max_hours', 'horsepower'],
    
    specificFields: ['horsepower', 'hours_used', 'engine_type', 'fuel_type', 'cylinders'],
    
    cardFields: [
      { key: 'horsepower', label: 'Potenza', suffix: 'HP' },
      { key: 'hours_used', label: 'Ore', format: (v) => v ? `${v}h` : 'N/A' },
      { key: 'year', label: 'Anno' },
      { key: 'dealer_price', label: 'Prezzo', format: (v) => `€${Number(v).toLocaleString('it-IT')}` },
    ],
    
    sortOptions: [
      { value: 'created_desc', label: 'Più recenti' },
      { value: 'created_asc', label: 'Meno recenti' },
      { value: 'price_asc', label: 'Prezzo crescente' },
      { value: 'price_desc', label: 'Prezzo decrescente' },
      { value: 'year_desc', label: 'Anno decrescente' },
      { value: 'hours_asc', label: 'Ore crescenti' },
      { value: 'hours_desc', label: 'Ore decrescenti' },
      { value: 'distance_asc', label: 'Distanza crescente' },
    ],
    
    filterComponent: 'MotorFilters',
    icon: 'gauge',
    themeColor: 'green',
  },
  
  accessories: {
    apiEndpoint: '/accessories',
    singular: 'accessorio',
    plural: 'accessori',
    singularCapitalized: 'Accessorio',
    pluralCapitalized: 'Accessori',
    
    specificFilters: ['category'],
    
    specificFields: ['category', 'compatibility', 'brand_compatibility', 'optional_features'],
    
    cardFields: [
      { key: 'category', label: 'Categoria' },
      { key: 'dealer_price', label: 'Prezzo', format: (v) => `€${Number(v).toLocaleString('it-IT')}` },
    ],
    
    sortOptions: [
      { value: 'created_desc', label: 'Più recenti' },
      { value: 'price_asc', label: 'Prezzo crescente' },
      { value: 'price_desc', label: 'Prezzo decrescente' },
    ],
    
    filterComponent: 'AccessoryFilters',
    icon: 'package',
    themeColor: 'purple',
  },
};

/**
 * Ottiene la configurazione per un tipo di item
 */
export function getItemConfig(itemType: ItemType): ItemConfig {
  return itemConfigs[itemType];
}

/**
 * Ottiene tutti i tipi di item disponibili
 */
export function getAvailableItemTypes(): ItemType[] {
  return Object.keys(itemConfigs) as ItemType[];
}

/**
 * Verifica se un tipo è valido
 */
export function isValidItemType(type: string): type is ItemType {
  return type in itemConfigs;
}

/**
 * Configurazione filtri comuni (condivisi tra tutti i tipi)
 */
export const commonFilters = [
  'brand',
  'model',
  'min_price',
  'max_price',
  'year',
  'engine_type',
  'fuel_type',
  'condition',
];

/**
 * Coordinate default (Lecce) - now loaded from API via geoConfig
 * Use getAdminCoords() from './geoConfig' instead
 */
export const DEFAULT_COORDS = { lat: 40.3516, lng: 18.1723 };

/**
 * Calcola la distanza tra due coordinate in km
 */
export function calcDistance(
  fromLat: number,
  fromLng: number,
  toLat: number,
  toLng: number
): number {
  const R = 6371; // Earth's radius in km
  const dLat = (toLat - fromLat) * Math.PI / 180;
  const dLng = (toLng - fromLng) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(fromLat * Math.PI / 180) * Math.cos(toLat * Math.PI / 180) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c);
}

/**
 * Helper per classi grid responsive
 */
export function getGridClass(cols: number): string {
  const classes = ['grid', 'grid-cols-1'];
  if (cols >= 2) classes.push('md:grid-cols-2');
  if (cols >= 3) classes.push('lg:grid-cols-3');
  if (cols >= 4) classes.push('xl:grid-cols-4');
  classes.push('gap-6');
  return classes.join(' ');
}